import cn from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
// @ts-expect-error: types for this packages doesn't know how to import the novtt bundle
import videojs from 'video.js/dist/alt/video.core.novtt';

import type Player_ from 'video.js/dist/types/player';
import type Videojs from 'video.js/dist/types/video';

import 'video.js/dist/video-js.css';

export type Player = Player_;

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
videojs.options.autoSetup = false;

export interface VideoPlayerProps {
  options: Partial<{
    aspectRatio: string;
    autoplay: boolean | 'muted' | 'play' | 'any';
    controls: boolean;
    disablePictureInPicture: boolean;
    enableSmoothSeeking: boolean;
    experimentalSvgIcons: boolean;
    fluid: boolean;
    fullscreen: { options: { navigationUI: 'auto' | 'show' | 'hide' } };
    html5: Partial<{
      nativeControlsForTouch: boolean;
      nativeTextTracks: boolean;
      preloadTextTracks: boolean;
    }>;
    loop: boolean;
    muted: boolean;
    nativeControlsForTouch: boolean;
    normalizeAutoplay: boolean;
    playsinline: boolean;
    poster: string | undefined;
    preload: 'auto' | 'metadata' | 'none';
    responsive: boolean;
    sources: { src: string; type: string }[];
    src: string;
    textTrackSettings: boolean;
    userActions: Partial<{
      click: boolean | ((e: Event) => void);
      doubleClick: boolean | ((e: Event) => void);
    }>;
    // Full list: https://videojs.com/guides/options/
    [key: string]: unknown;
  }>;
  onReady: (player: Player) => void;
  className?: string;
}

export const VideoJS = ({ options, onReady, className }: VideoPlayerProps) => {
  const [videoNode, setVideoNode] = useState<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const setVideoRef = useCallback((newVideoNode: HTMLDivElement | null) => {
    setVideoNode((oldVideoNode) => {
      // Dispose the Video.js player when the component unmounts or ref changes
      if (!newVideoNode || newVideoNode != oldVideoNode) {
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
      }
      return newVideoNode;
    });
  }, []);

  useEffect(() => {
    if (!videoNode) {
      return;
    }

    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement('video-js');
      videoNode.appendChild(videoElement);
      const player = (videojs as typeof Videojs)(videoElement, options, () => {
        onReady(player);
      });
      playerRef.current = player;
    } else {
      playerRef.current.autoplay(options.autoplay);
      playerRef.current.poster(options.poster);
      playerRef.current.src(options.sources);
    }
  }, [videoNode, options, onReady]);

  return <div data-vjs-player ref={setVideoRef} className={cn(className)}></div>;
};
